/**
 * Batch E (T11): GPS Point Decimation for Map Display
 * 
 * Douglas-Peucker line simplification + max-point limiter.
 * Reduces thousands of GPS points to a visually equivalent polyline
 * while preserving shape fidelity.
 */

/**
 * Perpendicular distance from point C to line segment AB (in degrees).
 */
function perpendicularDistance(C, A, B) {
  const dx = B.lng - A.lng;
  const dy = B.lat - A.lat;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) {
    const ex = C.lng - A.lng;
    const ey = C.lat - A.lat;
    return Math.sqrt(ex * ex + ey * ey);
  }
  const t = Math.max(0, Math.min(1, ((C.lng - A.lng) * dx + (C.lat - A.lat) * dy) / lenSq));
  const projLng = A.lng + t * dx;
  const projLat = A.lat + t * dy;
  const ex = C.lng - projLng;
  const ey = C.lat - projLat;
  return Math.sqrt(ex * ex + ey * ey);
}

/**
 * Douglas-Peucker line simplification.
 * @param {Array} points - [{lat, lng, ...}]
 * @param {number} epsilon - tolerance in degrees (~0.00005 ≈ 5m)
 * @returns {Array} simplified points
 */
function douglasPeucker(points, epsilon) {
  if (points.length <= 2) return points;
  
  let maxDist = 0;
  let maxIdx = 0;
  const first = points[0];
  const last = points[points.length - 1];
  
  for (let i = 1; i < points.length - 1; i++) {
    const dist = perpendicularDistance(points[i], first, last);
    if (dist > maxDist) {
      maxDist = dist;
      maxIdx = i;
    }
  }
  
  if (maxDist > epsilon) {
    const left = douglasPeucker(points.slice(0, maxIdx + 1), epsilon);
    const right = douglasPeucker(points.slice(maxIdx), epsilon);
    return left.slice(0, -1).concat(right);
  }
  
  return [first, last];
}

/**
 * Decimate GPS points for polyline display.
 * First applies Douglas-Peucker, then limits to maxPoints via uniform sampling.
 *
 * @param {Array} points - Raw GPS points [{lat, lng, ...}]
 * @param {number} maxPoints - Maximum output points (default 500)
 * @param {number} epsilon - DP tolerance in degrees (default ~5m)
 * @returns {Array} decimated points
 */
export function decimatePoints(points, maxPoints = 500, epsilon = 0.00005) {
  if (!points || points.length <= maxPoints) return points;
  
  // Step 1: Douglas-Peucker simplification
  const simplified = douglasPeucker(points, epsilon);
  
  // Step 2: If still too many, uniform sample
  if (simplified.length <= maxPoints) return simplified;
  
  const step = (simplified.length - 1) / (maxPoints - 1);
  const result = [];
  for (let i = 0; i < maxPoints - 1; i++) {
    result.push(simplified[Math.round(i * step)]);
  }
  result.push(simplified[simplified.length - 1]); // Always include last
  return result;
}

/**
 * Quick distance-based filter: skip points closer than minDistDeg.
 * Faster than DP for very large datasets.
 */
export function filterByDistance(points, minDistDeg = 0.0001) {
  if (!points || points.length <= 2) return points;
  
  const result = [points[0]];
  let last = points[0];
  
  for (let i = 1; i < points.length - 1; i++) {
    const dx = points[i].lng - last.lng;
    const dy = points[i].lat - last.lat;
    if (dx * dx + dy * dy >= minDistDeg * minDistDeg) {
      result.push(points[i]);
      last = points[i];
    }
  }
  
  result.push(points[points.length - 1]); // Always include last
  return result;
}
